import { colors, widgets } from "@/domain/constants.ts";
import {
  IDashboardStore,
  ILayout,
} from "@/domain/interfaces/dashboard.interface.ts";
import { cloneDeep, last } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
import {
  createJSONStorage,
  devtools,
  persist,
  subscribeWithSelector,
} from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { GLOBAL_FILTERS, MAX_LAYOUTS_PER_PLAN } from "@/domain/constants";
import { useUserStore } from "@/lib/stores/userStore";
import { toast } from "@/components/ui/use-toast";
import { StudyInputValue } from "@tradingView/charting_library";
import { useMarketplaceStore } from "./stores/useMarketplaceStore";
import { MANUAL_COLOR } from "@/domain/constants";
import { environment } from "./constants";
import { reconnectNATS } from "@/lib/natsConnection";

interface DrawSignalState {
  signalToDraw: {
    signal: string | null;
    color: string | null;
    inputs?: Record<string, StudyInputValue>;
  };
  setSignalToDraw: (
    signal: string | null,
    color: string | null,
    inputs?: Record<string, StudyInputValue>,
  ) => void;
  clearSignal: () => void;
}

export const useDrawSignal = create<DrawSignalState>((set) => ({
  signalToDraw: { signal: null, color: null, inputs: {} },
  setSignalToDraw: (signal, color, inputs) =>
    set({ signalToDraw: { signal, color, inputs } }),
  clearSignal: () =>
    set({ signalToDraw: { signal: null, color: null, inputs: {} } }),
}));

const emptyLayout: ILayout = {
  name: "New Layout",
  id: "",
  createdAt: Date.now(),
  json: {
    type: "row",
    weight: 100,
    children: [],
  },
};

const initialLayouts: ILayout[] = [
  {
    name: "Initial Layout",
    id: "initial-layout",
    createdAt: Date.now(),
    json: {
      type: "row",
      id: "#de7dbc64-a10f-408b-a02b-a7d61fe39ed1",
      children: [
        {
          type: "tabset",
          id: "#d0f53544-0cc9-4d9c-a069-a966fdcbe8e3",
          weight: 25,
          children: [
            {
              type: "tab",
              id: "#initChart",
              name: "chart",
              component: "chart",
              config: widgets.chart.config,
            },
          ],
        },
        {
          type: "tabset",
          id: "#8ebadcbc-c602-4b51-a547-a87eb9899c52",
          weight: 25,
          children: [
            {
              type: "tab",
              id: "#initBinanceTop",
              name: "binanceTop",
              component: "binanceTop",
              config: widgets.binanceTop.config,
            },
          ],
          active: true,
        },
      ],
    },
  },
];

export const useDashboard = create<IDashboardStore>()(
  // TODO move to useDashboard.ts hook
  subscribeWithSelector(
    persist(
      devtools(
        immer((set, get) => ({
          server: environment.socketUrl.eu,
          layouts: initialLayouts,
          layoutsOrder: ["initial-layout"],
          activeLayoutId: "initial-layout",
          colorsToSymbolMap: {
            [colors[0]]: "BTCUSDT",
          },
          indicatorsToColorMap: {},
          settings: {},
          favorites: [],
          globalFilters: GLOBAL_FILTERS,
          blacklist: [],
          favoritesSettings: {
            activeColumns: [],
          },
          pinned: [],
          natsReconnected: false,
          ackNatsReconnected: () => set({ natsReconnected: false }),
          setActiveLayoutId: (layoutId) => {
            const layout = get().layouts.find((l) => l.id === layoutId);
            if (!layout) {
              console.error("Layout not found", layoutId);
              return;
            }
            set((state) => {
              state.activeLayoutId = layoutId;
            });
          },
          setServer: (server) => {
            set((state) => {
              if (server === "asia") {
                state.server = environment.socketUrl.asia;
              } else {
                state.server = environment.socketUrl.eu;
              }
            });
            reconnectNATS(get().server).then(() => {
              set({ natsReconnected: true });
            });
          },
          addLayout: async (_layout = emptyLayout) => {
            const userRole = useUserStore.getState().user?.role || "GUEST";
            const currentLayouts = get().layouts.length;
            let maxLayouts = MAX_LAYOUTS_PER_PLAN[userRole.toUpperCase()] ?? 1;
            const purchasedProductIds =
              useMarketplaceStore.getState().purchasedProductIds;
            if (purchasedProductIds.includes("gSu7mYLXllxTYfWhGPMF")) {
              maxLayouts = 10;
            }
            if (purchasedProductIds.includes("ILXIHcSCBCTmAjLtxrxz")) {
              maxLayouts = 10;
            }
            if (purchasedProductIds.includes("mvuRebngQzG296aE9Szg")) {
              maxLayouts = 10;
            }
            if (purchasedProductIds.includes("xSL4BDy3i6vQKnjfv1x1")) {
              maxLayouts = 10;
            }

            if (currentLayouts >= maxLayouts) {
              toast({
                title: "Layout limit reached",
                description: `Your ${userRole} plan allows up to ${maxLayouts} layouts. Upgrade your plan for more layouts.`,
                variant: "destructive",
              });
              return;
            }

            const layout = cloneDeep(_layout);
            if (!layout.id.length) {
              layout.id = uuidv4();
            }
            layout.createdAt = Date.now();

            set((state) => {
              state.layouts.push(layout);
              state.activeLayoutId = layout.id;
              state.layoutsOrder = [...state.layoutsOrder, layout.id];
            });
          },
          updateLayout: async (layout) => {
            set((state) => {
              const index = state.layouts.findIndex((l) => l.id === layout.id);
              if (index === -1) {
                console.error("Layout not found", layout.id);
                return;
              }
              state.layouts[index] = layout;
            });
          },
          removeActiveLayout: async () => {
            const oldState = get();
            const idToRemove = oldState.activeLayoutId;

            if (!idToRemove || oldState.layouts.length <= 1) {
              console.error("Can't remove the last layout");
              return;
            }

            const filteredLayoutsOrder = oldState.layoutsOrder.filter(
              (id) => id !== idToRemove,
            );
            const newActiveLayoutId = last(filteredLayoutsOrder);

            set((state) => {
              if (newActiveLayoutId) {
                const activeLayout = state.layouts.find(
                  (layout) => layout.id === newActiveLayoutId,
                ) as ILayout;
                if (!activeLayout) {
                  console.error("Active layout not found", newActiveLayoutId);
                  return;
                }
                state.activeLayoutId = newActiveLayoutId;

                state.layoutsOrder = filteredLayoutsOrder;
              }
              state.layouts = oldState.layouts.filter(
                (layout) => layout.id !== idToRemove,
              );
            });
          },
          updateActiveLayout: async (cb) => {
            set((state) => {
              const activeLayout = state.layouts.find(
                (layout) => layout.id === state.activeLayoutId,
              );
              if (!activeLayout) {
                console.error("Active layout not found", state.activeLayoutId);
                return;
              }
              cb(activeLayout);
            });
          },
          resetLayouts: async () => {
            set((state) => {
              state.layouts = initialLayouts;
              state.activeLayoutId = "initial-layout";
              state.layoutsOrder = ["initial-layout"];
            });
          },
          updateDashboardState: async (cb) => {
            set(cb);
          },
          updatePinned: (cb) => {
            set((state) => {
              const newPinned = cb(state.pinned);
              return { ...state, pinned: newPinned };
            });
          },
          addFavoriteFolder: (folder) => {
            set((state) => {
              state.favorites.push({
                id: uuidv4(),
                ...folder,
                expanded: true,
                symbols: folder.symbols ? [...folder.symbols] : [],
              });
            });
          },
          removeFavoriteFolder: (folderId) => {
            set((state) => {
              state.favorites = state.favorites.filter(
                (f) => f.id !== folderId,
              );
            });
          },
          addSymbolToBlacklist: (symbol) => {
            set((state) => {
              state.blacklist.push(symbol);
            });
          },
          removeSymbolFromBlacklist: (symbol) => {
            set((state) => {
              state.blacklist = state.blacklist.filter((s) => s !== symbol);
            });
          },
          updateFavorites: (cb) => {
            set((state) => {
              cb(state.favorites);
            });
          },
          updateFavoritesSettings: (cb) => {
            set((state) => {
              cb(state.favoritesSettings);
            });
          },
          addSymbolsToFavorites: (folderId, symbolNames) => {
            set((state) => {
              const folder = state.favorites.find((f) => f.id === folderId);
              if (!folder) {
                // create new folder
                state.favorites.push({
                  id: uuidv4(),
                  name: "New Folder",
                  color: "",
                  expanded: true,
                  symbols: symbolNames,
                });
              } else {
                folder.symbols = [...folder.symbols, ...symbolNames];
              }
            });
          },
          removeSymbolFromFavorites: (folderId, symbolId) => {
            set((state) => {
              const folder = state.favorites.find((f) => f.id === folderId);
              if (!folder) {
                return;
              }
              folder.symbols = folder.symbols.filter((s) => s !== symbolId);
            });
          },
          setSymbolForColor: (color, symbol) => {
            set((state) => {
              if (color === MANUAL_COLOR) {
                return;
              }
              state.colorsToSymbolMap[color] = symbol;
            });
          },
          addIndicatorForColor: (color, indicator) => {
            set((state) => {
              state.indicatorsToColorMap[color] = [
                ...(state.indicatorsToColorMap[color] || []),
                indicator,
              ];
            });
          },
          getGlobalFilterValues: () => {
            return get().globalFilters.reduce(
              (acc, filter) => {
                acc[filter.filterByField] = filter.value;
                return acc;
              },
              {} as Record<string, string>,
            );
          },
          updateGlobalFilter: (index: number, value: string) =>
            set((state) => ({
              globalFilters: state.globalFilters.map((filter, i) =>
                i === index ? { ...filter, value } : filter,
              ),
            })),
        })),
      ),
      {
        version: 2,
        name: "sf-state",
        storage: createJSONStorage(() => localStorage),
        migrate: (persistedState: unknown, version: number) => {
          const state = persistedState as IDashboardStore | undefined;
          return {
            ...state,
            pinned: [],
          };
        },
      },
    ),
  ),
);
